import { useState } from 'react';
import { Alert, Button, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { settings } from '../config/settings';
import { apiFetch } from '../utils/apiClient';

const COURSES_ENDPOINT = settings?.api?.baseUrl && settings?.api?.courses?.apiEndpoint 
    ? `${settings.api.baseUrl}${settings.api.courses.apiEndpoint}` 
    : null;

export const Courses = () => {
    const { user, getIdToken } = useAuth();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const fetchCourses = async () => {
        if (!COURSES_ENDPOINT) {
            setError('No hay un endpoint de cursos configurado. Revisa config/settings.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getIdToken();
            if (!token) {
                throw new Error('No fue posible obtener el token del usuario autenticado.');
            }

            const response = await apiFetch(COURSES_ENDPOINT, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const message = await response.text();
                throw new Error(message || `La API devolvió ${response.status}`);
            }

            const data = await response.json();
            setCourses(Array.isArray(data) ? data : []);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.message ?? 'Ocurrió un error al consultar los cursos.');
            setCourses([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return 'N/D';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString();
    };

    return (
        <div className="mt-4">
            <h3>Cursos disponibles</h3>
            <p className="text-muted">
                Esta información se obtiene en tiempo real desde el backend en{' '}
                <code>{COURSES_ENDPOINT || `${settings?.api?.baseUrl}${settings?.api?.courses?.apiEndpoint}`}</code>.
            </p>

            <Button variant="primary" onClick={fetchCourses} disabled={loading}>
                {loading ? (
                    <>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                        Consultando...
                    </>
                ) : (
                    'Ver cursos'
                )}
            </Button>

            {lastUpdated && !loading && !error && (
                <p className="text-muted mt-2">
                    Última actualización: {lastUpdated.toLocaleString()}
                </p>
            )}

            {error && (
                <Alert variant="danger" className="mt-3" onClose={() => setError(null)} dismissible>
                    <Alert.Heading>Ups, algo falló</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            )}

            {!error && courses.length === 0 && !loading && (
                <Alert variant="info" className="mt-3">
                    Aún no hemos cargado cursos. Presiona “Ver cursos” para traerlos desde el backend.
                </Alert>
            )}

            {courses.length > 0 && (
                <div className="mt-3">
                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th>Título</th>
                                <th>Descripción</th>
                                <th>Estado</th>
                                <th>Actualizado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {courses.map((course) => (
                                <tr key={course.externalId ?? course.id ?? course.title}>
                                    <td>{course.title}</td>
                                    <td>{course.description || 'Sin descripción'}</td>
                                    <td>{course.status ?? 'Desconocido'}</td>
                                    <td>{formatDate(course.updatedAt || course.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {!user && (
                <Alert variant="warning" className="mt-3">
                    Debes iniciar sesión para consultar cursos.
                </Alert>
            )}
        </div>
    );
};